import React from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { useTranslation } from "react-i18next"
import { CmsImage, Container, Button } from "@bw/bits"
import { motion } from "framer-motion"
import { breakpoints } from "../../../theme"

const variants = {
  open: { opacity: 1, display: "flex", justifyContent: "flex-end" },
  closed: { opacity: 0, transitionEnd: { display: "none" } },
}

const innerVariants = {
  open: { opacity: 1, display: "flex" },
  closed: { opacity: 0, transitionEnd: { display: "none" } },
}

const Layer = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 9;
`

const InnerLayer = styled(motion.div)`
  background-color: #111;
  width: 100%;
  margin: auto;
  padding: 0;
  aspect-ratio: 16 / 9;
  width: 90vw;
  max-width: 800px;
`

const HeroWrapper = styled.div`
  position: relative;
  width: 100%;
  padding: 100px 0 0;

  @media (min-width: ${breakpoints.medium}px) {
    min-height: 580px;
    padding: 180px 0 0 0;
  }

  svg {
    margin-top: -60px;
    position: relative;
  }

  text-align: center;
  .gatsby-image-wrapper {
    @media (min-width: ${breakpoints.medium}px) {
      padding: 0 60px;
    }
  }
`

const Video = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const StyledHeading = styled.div`
  text-align: left;
  max-width: 640px;
  line-height: 1.2;
  padding-right: 65px;
  color: var(--text-color);
`

const Suptitle = styled.div`
  font-weight: 500;
  font-size: 22px;
  line-height: 26px;

  @media (min-width: ${breakpoints.small}px) {
    font-size: 22px;
  }
`

const Title = styled.h1`
  margin: 0;
  line-height: 1.2;
  font-weight: 700;
  font-size: 36px;

  @media (min-width: ${breakpoints.small}px) {
    font-size: 45px;
  }
`

const Subtitle = styled.div`
  max-width: 445px;
  font-weight: 400;
  font-size: 16px;
  line-height: 172.5%;

  @media (min-width: ${breakpoints.small}px) {
    font-size: 16px;
  }
`

const Buttons = styled.div`
  margin-top: 30px;
  max-width: 100%;
  justify-content: ${props => (props.justify ? props.justify : "")};

  > * + * {
    margin-left: 20px;
  }
`

const Hero = ({
  suptitle,
  title,
  subtitle,
  icon,
  containerSize,
  negative,
  buttons,
  buttonsPosition,
  image,
  video,
  videoPopup,
}) => {
  const [show, setShow] = React.useState(false)
  const { t } = useTranslation()
  const heroRef = React.useRef(null)
  const layerRef = React.useRef(null)
  const videoRef = React.useRef(null)

  const closeOnOutsideClick = React.useCallback(
    (e, ref) => {
      if (e.target === ref.current) {
        setShow(false)
      }
    },
    [setShow]
  )

  React.useEffect(() => {
    const handleEsc = event => {
      if (event.keyCode === 27) {
        setShow(false)
      }
    }

    window.addEventListener("keydown", handleEsc)

    return () => {
      window.removeEventListener("keydown", handleEsc)
    }
  }, [])

  React.useEffect(() => {
    if (videoPopup && videoRef.current) {
      if (show) {
        videoRef.current.play()
      } else {
        videoRef.current.pause()
      }
    }
  }, [videoPopup, videoRef, show])

  return (
    <Container size={containerSize}>
      <HeroWrapper ref={heroRef}>
        {video && (
          <Video autoPlay muted loop>
            <source src={video} type="video/mp4" />
            <track kind="captions" />
          </Video>
        )}
        {image && (
          <CmsImage
            loading="eager"
            src={image}
            alt={title}
            objectFit="contain"
          />
        )}
        {icon}
        <StyledHeading negative={negative}>
          {suptitle && <Suptitle>{suptitle}</Suptitle>}
          <Title>{title}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </StyledHeading>
        {(buttons || videoPopup) && (
          <Buttons justify={buttonsPosition}>
            {buttons}
            {videoPopup && (
              <Button
                primary
                onClick={() => setShow(true)}
                inversed
                label={"▶ " + t("hero.playVideo")}
              />
            )}
          </Buttons>
        )}
        {/*
        <ScrollDown
          onClick={() => {
            window.scrollTo({
              top: heroRef.current.offsetHeight,
              behavior: "smooth",
            })
          }}
        >
          {t("scrolldown")}
        </ScrollDown>
*/}
      </HeroWrapper>

      {videoPopup && (
        <Layer
          animate={show ? "open" : "closed"}
          variants={variants}
          transition={{ duration: 0.4 }}
          initial={false}
          ref={layerRef}
          onClick={e => closeOnOutsideClick(e, layerRef)}
        >
          <InnerLayer
            animate={show ? "open" : "closed"}
            variants={innerVariants}
            transition={{ duration: 0.6 }}
            initial={false}
          >
            <video controls autoPlay ref={videoRef}>
              <source src={videoPopup} type="video/mp4" />
              <track kind="captions" />
            </video>
          </InnerLayer>
        </Layer>
      )}
    </Container>
  )
}

Hero.propTypes = {
  negative: PropTypes.bool,
  suptitle: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  buttons: PropTypes.arrayOf(PropTypes.element),
  icon: PropTypes.element,
  image: PropTypes.object.isRequired,
  video: PropTypes.string,
  videoPopup: PropTypes.string,
  buttonsPosition: PropTypes.oneOf(["start", "center", "end"]),
  containerSize: PropTypes.oneOf(["small", "medium", "large"]),
}

Hero.defaultProps = {
  buttonsPosition: "start",
  containerSize: "medium",
  negative: true,
}

export { Hero }
